import { useEffect, useMemo, useState } from 'react';
import * as adminService from '@/services/adminService';

const roleLabels = {
  player: 'Jogador',
  admin: 'Admin',
  partner: 'Parceiro',
  super_admin: 'Super admin',
};

const roleStyles = {
  player: 'border-ocean-light/30 bg-ocean-light/10 text-ocean-light',
  admin: 'border-accent/30 bg-accent/10 text-accent',
  partner: 'border-forest-light/30 bg-forest-light/10 text-forest-light',
  super_admin: 'border-gem/30 bg-gem/10 text-gem',
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-PT');
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((user) => user.isActive).length,
    admins: users.filter((user) => ['admin', 'super_admin'].includes(user.role)).length,
    players: users.filter((user) => user.role === 'player').length,
  }), [users]);

  const filteredUsers = useMemo(() => {
    if (filter === 'all') return users;
    if (filter === 'admins') return users.filter((user) => ['admin', 'super_admin'].includes(user.role));
    return users.filter((user) => user.role === filter);
  }, [filter, users]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await adminService.getUsers({ limit: 100 });
      setUsers(res.data.data || []);
      setError('');
    } catch {
      setError('Não foi possível carregar os utilizadores.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <section className="min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">Comunidade</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Utilizadores</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">
            Consulta contas, perfis associados e progresso essencial dos jogadores.
          </p>
        </div>
        <button type="button" onClick={loadData} className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-bold text-accent hover:bg-accent/20">
          Atualizar
        </button>
      </div>

      {error && (
        <div className="mt-5 rounded-lg border border-energy/40 bg-energy/10 p-4 text-sm font-bold text-energy">{error}</div>
      )}

      <div className="mt-6 grid gap-3 md:grid-cols-4">
        {[
          ['Total', stats.total],
          ['Ativos', stats.active],
          ['Admins', stats.admins],
          ['Jogadores', stats.players],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border border-fg/10 bg-fg/[0.04] p-4">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-fg/35">{label}</div>
            <div className="mt-2 font-serif text-3xl font-black text-accent">{value}</div>
          </div>
        ))}
      </div>

      <section className="mt-6 rounded-lg border border-fg/10 bg-fg/[0.04]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-fg/10 p-4">
          <h2 className="font-serif text-xl font-black text-accent">Contas registadas</h2>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'all', label: 'Todos' },
              { value: 'player', label: 'Jogadores' },
              { value: 'admins', label: 'Admins' },
              { value: 'partner', label: 'Parceiros' },
            ].map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setFilter(item.value)}
                className={`rounded-full border px-3 py-1 text-xs font-bold ${filter === item.value ? 'border-accent/40 bg-accent/15 text-accent' : 'border-fg/10 bg-black/20 text-fg/50 hover:text-fg'}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.16em] text-fg/35">
              <tr>
                <th className="px-4 py-3">Conta</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Jogador</th>
                <th className="px-4 py-3">Nível</th>
                <th className="px-4 py-3">Economia</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Criado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {!loading && filteredUsers.length === 0 && (
                <tr><td className="px-4 py-6 text-fg/45" colSpan="7">Nenhum utilizador encontrado.</td></tr>
              )}
              {filteredUsers.map((user) => (
                <tr key={user.id} className="align-top">
                  <td className="px-4 py-3">
                    <div className="font-bold text-fg">{user.email}</div>
                    <div className="mt-1 max-w-[220px] truncate font-mono text-xs text-fg/30">{user.id}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-3 py-1 text-xs font-bold ${roleStyles[user.role] || roleStyles.player}`}>
                      {roleLabels[user.role] || user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-bold text-fg/80">{user.player?.displayName || user.player?.username || '-'}</div>
                    <div className="mt-1 text-xs text-fg/35">{user.player?.username || 'Sem perfil de jogador'}</div>
                  </td>
                  <td className="px-4 py-3 text-fg/60">
                    {user.player ? `Nível ${user.player.level || 1}` : '-'}
                    <div className="mt-1 text-xs text-fg/35">{user.player?.xp ?? 0} XP</div>
                  </td>
                  <td className="px-4 py-3 text-fg/60">
                    {user.player ? `${user.player.coins ?? 0} moedas` : '-'}
                    <div className="mt-1 text-xs text-fg/35">{user.player?.gems ?? 0} gemas</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-3 py-1 text-xs font-bold ${user.isActive ? 'border-forest-light/30 bg-forest-light/10 text-forest-light' : 'border-fg/15 bg-fg/5 text-fg/45'}`}>
                      {user.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-fg/60">{formatDate(user.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}
